/**
 * Public tenant config for login / boot: branding + login methods from hostname.
 * Backend resolves tenant from Host header; slug query param is informational only.
 */

import type { TenantBrandingProfile, TenantThemeColors } from './tenantBranding';
import { TENANT_PROFILES } from './tenantBranding';

export type LoginMethod = 'password' | 'sso';

export type SsoProvider = 'saml' | 'entra' | 'okta';

export type PublicTenantConfigDTO = {
  tenantId: string | null;
  tenantName: string;
  logoUrl: string;
  logoMarkUrl: string;
  logoOnDarkUrl: string;
  primaryColor: string;
  secondaryColor: string;
  welcomeText: string;
  loginMethods: LoginMethod[];
  ssoProvider: SsoProvider | null;
  ssoLoginUrl: string | null;
  ssoDisabledHint: string;
  supportEmail: string | null;
  isDefaultTenant: boolean;
};

const CACHE_TTL_MS = 60_000;
/** Avoid multi‑MB sessionStorage writes that freeze the main thread on parse/stringify. */
const MAX_CACHED_LOGO_CHARS = 2048;

type CachedEntry = { loadedAt: number; config: PublicTenantConfigDTO };

function trimLogoForCache(url: string): string {
  if (!url) return url;
  if (url.startsWith('data:') && url.length > MAX_CACHED_LOGO_CHARS) return '';
  return url;
}

/** Strip inline mega‑logos before persisting; full URLs still cached. */
export function sanitizeConfigForCache(config: PublicTenantConfigDTO): PublicTenantConfigDTO {
  return {
    ...config,
    logoUrl: trimLogoForCache(config.logoUrl),
    logoMarkUrl: trimLogoForCache(config.logoMarkUrl),
    logoOnDarkUrl: trimLogoForCache(config.logoOnDarkUrl)
  };
}

let memoryCache: CachedEntry | null = null;

function configCacheKey(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname : 'default';
  // v2: invalidate caches that stored raw Okta IdP URLs (wrong ACS after login).
  return `turboiam-public-tenant-config:v2:${host}`;
}

/** Derive tenant slug from hostname for optional query param (backend still uses Host). */
export function detectTenantSlugFromHost(): string | null {
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  if (!host || host === 'localhost' || host === '127.0.0.1') return null;

  const envTenant = (process.env.REACT_APP_TENANT_ID || '').trim().toLowerCase();
  if (envTenant) return envTenant;

  if (host === 'demo.provysn.ai' || host.endsWith('.demo.provysn.ai')) return 'provysn';

  const labels = host.split('.');
  if (labels.length >= 3 && labels[labels.length - 2] === 'provysn') {
    const sub = labels[0];
    if (sub && sub !== 'www' && sub !== 'demo' && sub !== 'turboiam') return sub;
  }

  if (host.includes('turboiam-')) {
    const m = host.match(/turboiam-([a-z0-9-]+)-dev-fe/);
    if (m?.[1]) return m[1];
  }

  const known = ['mercury', 'venus', 'mars', 'jupiter', 'provysn'];
  for (const id of known) {
    if (host.includes(id)) return id;
  }

  return null;
}

export function configToBrandingProfile(cfg: PublicTenantConfigDTO): TenantBrandingProfile {
  const tenantId = cfg.tenantId || detectTenantSlugFromHost() || 'default';
  const staticTheme =
    TENANT_PROFILES[tenantId]?.themeColors || TENANT_PROFILES.default.themeColors;
  const themeColors: TenantThemeColors = {
    primary: cfg.primaryColor,
    secondary: cfg.secondaryColor,
    light: staticTheme.light,
    dark: staticTheme.dark,
    contrastText: staticTheme.contrastText
  };
  return {
    tenantId,
    storageKey: `${tenantId}-branding`,
    companyName: cfg.tenantName,
    companyTagline: cfg.welcomeText,
    logo: cfg.logoUrl,
    logoMark: cfg.logoMarkUrl,
    logoOnDark: cfg.logoOnDarkUrl,
    themeColors,
    isDefaultTenant: cfg.isDefaultTenant
  };
}

function readSessionCache(): PublicTenantConfigDTO | null {
  try {
    const key = configCacheKey();
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    if (raw.length > 100_000) {
      sessionStorage.removeItem(key);
      return null;
    }
    const parsed = JSON.parse(raw) as CachedEntry;
    if (Date.now() - parsed.loadedAt > CACHE_TTL_MS) return null;
    return parsed.config;
  } catch {
    return null;
  }
}

function writeSessionCache(config: PublicTenantConfigDTO): void {
  try {
    const entry: CachedEntry = { loadedAt: Date.now(), config: sanitizeConfigForCache(config) };
    sessionStorage.setItem(configCacheKey(), JSON.stringify(entry));
  } catch {
    /* ignore */
  }
}

export function clearTenantConfigCache(): void {
  memoryCache = null;
  try {
    sessionStorage.removeItem(configCacheKey());
  } catch {
    /* ignore */
  }
}

export function getCachedTenantConfig(): PublicTenantConfigDTO | null {
  if (memoryCache && Date.now() - memoryCache.loadedAt < CACHE_TTL_MS) {
    return memoryCache.config;
  }
  const fromSession = readSessionCache();
  if (fromSession) {
    memoryCache = { loadedAt: Date.now(), config: fromSession };
    return fromSession;
  }
  return null;
}

function defaultConfig(): PublicTenantConfigDTO {
  const profile = TENANT_PROFILES[detectTenantSlugFromHost() || ''] || TENANT_PROFILES.default;
  return {
    tenantId: profile.tenantId === 'default' ? null : profile.tenantId,
    tenantName: profile.companyName,
    logoUrl: profile.logo,
    logoMarkUrl: profile.logoMark,
    logoOnDarkUrl: profile.logoOnDark,
    primaryColor: profile.themeColors.primary,
    secondaryColor: profile.themeColors.secondary,
    welcomeText: profile.companyTagline,
    loginMethods: ['password'],
    ssoProvider: null,
    ssoLoginUrl: null,
    ssoDisabledHint:
      'SSO is not configured for this tenant. An administrator can configure sign-in under Settings → SSO.',
    supportEmail: null,
    isDefaultTenant: profile.isDefaultTenant ?? true
  };
}

/** Load tenant config once per hostname (session + in-memory cache). */
export async function fetchAndCacheTenantConfig(): Promise<PublicTenantConfigDTO> {
  const cached = getCachedTenantConfig();
  if (cached) return cached;

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
  const slug = detectTenantSlugFromHost();
  const qs = slug ? `?slug=${encodeURIComponent(slug)}` : '';
  const url = `${apiBase}/api/v1/public/tenant-config${qs}`;

  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = (await res.json()) as { data?: PublicTenantConfigDTO };
    if (json?.data) {
      memoryCache = { loadedAt: Date.now(), config: json.data };
      writeSessionCache(json.data);
      return json.data;
    }
  } catch {
    /* fallback */
  }

  const fallback = defaultConfig();
  memoryCache = { loadedAt: Date.now(), config: fallback };
  writeSessionCache(fallback);
  return fallback;
}

/** Public login button label — generic; IdP name is not shown. */
export const SSO_LOGIN_BUTTON_LABEL = 'Sign in with SSO';
